import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { RBACProvider } from './contexts/RBACContext';
import { ProjectProvider } from './contexts/ProjectContext';
import DashboardLayout from './layouts/DashboardLayout';

// Pages - Lazy Loaded for Bundle Size Optimization
const ExecutiveDashboard = React.lazy(() => import('./pages/ExecutiveDashboard'));
const CompanyDashboard = React.lazy(() => import('./pages/CompanyDashboard'));
const ProjectManagementModule = React.lazy(() => import('./pages/ProjectManagementModule'));
const RoleManagementModule = React.lazy(() => import('./pages/RoleManagementModule'));
const UsersModule = React.lazy(() => import('./pages/UsersModule'));
const DPRModule = React.lazy(() => import('./pages/DPRModule'));
const PlanningEngine = React.lazy(() => import('./domains/planning/PlanningEngine'));
const LabourModule = React.lazy(() => import('./pages/LabourModule'));
const EquipmentModule = React.lazy(() => import('./pages/EquipmentModule'));
const FinanceModule = React.lazy(() => import('./pages/FinanceModule'));
const TestReportsModule = React.lazy(() => import('./pages/TestReportsModule'));
const ImportantDocumentsModule = React.lazy(() => import('./pages/ImportantDocumentsModule'));
const StoreModule = React.lazy(() => import('./pages/StoreModule'));
const WorkingModule = React.lazy(() => import('./pages/WorkingModule'));
const YardModule = React.lazy(() => import('./pages/YardModule'));
const BOQModule = React.lazy(() => import('./pages/BOQModule'));
const BatchingPlantModule = React.lazy(() => import('./pages/BatchingPlantModule'));
const AnalysisDashboardModule = React.lazy(() => import('./pages/AnalysisDashboardModule'));
const SubcontractorRateModule = React.lazy(() => import('./pages/SubcontractorRateModule'));
const ClientBillingModule = React.lazy(() => import('./pages/ClientBillingModule'));
const VillaSalesModule = React.lazy(() => import('./pages/VillaSalesModule'));
const DrawingsModule = React.lazy(() => import('./pages/DrawingsModule'));
const ProgressTrackingModule = React.lazy(() => import('./pages/ProgressTrackingModule/ProgressTrackingModule'));
const SalaryModule = React.lazy(() => import('./pages/SalaryModule'));
const BGModule = React.lazy(() => import('./pages/BGModule'));
const TenderManagementModule = React.lazy(() => import('./pages/TenderManagementModule'));
const ItemKnowledgeBaseModule = React.lazy(() => import('./pages/ItemKnowledgeBaseModule'));
const ProcurementVendorModule = React.lazy(() => import('./pages/ProcurementVendorModule'));
const AIIntegrationModule = React.lazy(() => import('./pages/AIIntegrationModule'));
const DocumentManagerModule = React.lazy(() => import('./pages/DocumentManagerModule'));
const AttendanceModule = React.lazy(() => import('./pages/AttendanceModule'));
const WeighbridgeModule = React.lazy(() => import('./pages/WeighbridgeModule'));
const DigitalViewerModule = React.lazy(() => import('./pages/DigitalViewerModule'));
const ConstructionActivityTrackerModule = React.lazy(() => import('./pages/ConstructionActivityTrackerModule/ConstructionActivityTrackerModule'));
const StoreDocumentExplorerModule = React.lazy(() => import('./pages/StoreDocumentExplorerModule'));
const WhatsAppIntelligenceModule = React.lazy(() => import('./pages/WhatsAppIntelligenceModule'));
const RecruitmentModule = React.lazy(() => import('./pages/RecruitmentModule/RecruitmentModule'));
const POManagementModule = React.lazy(() => import('./pages/POManagementModule'));
const TenderIntelligenceModule = React.lazy(() => import('./pages/TenderIntelligenceModule'));
const PublicApplicationPage = React.lazy(() => import('./pages/Careers/PublicApplicationPage'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';

const ProtectedRoute = () => {
  const { currentUser, loading } = useAuth();
  
  // Only redirect if we explicitly know they are logged out
  if (!loading && !currentUser) {
    return <Navigate to="/login" />;
  }
  
  // Always render the Dashboard layout frame. If loading, it will show a skeleton inside.
  return <DashboardLayout isAuthLoading={loading} />;
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <RBACProvider>
          <ProjectProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/privacy-policy" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><PrivacyPolicy /></Suspense>} />
              <Route path="/careers/job/:jobId" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><PublicApplicationPage /></Suspense>} />
              
              <Route path="/" element={<ProtectedRoute />}>
                <Route index element={<ExecutiveDashboard />} />
                <Route path="company-dashboard" element={<CompanyDashboard />} />
                <Route path="projects" element={<ProjectManagementModule />} />
                <Route path="roles" element={<RoleManagementModule />} />
                <Route path="users" element={<UsersModule />} />
                <Route path="dpr" element={<DPRModule />} />
                <Route path="planning" element={<PlanningEngine />} />
                <Route path="labour" element={<LabourModule />} />
                <Route path="equipment" element={<EquipmentModule />} />
                <Route path="finance" element={<FinanceModule />} />
                <Route path="test-reports" element={<TestReportsModule />} />
                <Route path="documents" element={<ImportantDocumentsModule />} />
                <Route path="store" element={<StoreModule />} />
                <Route path="store-document-explorer" element={<StoreDocumentExplorerModule />} />
                <Route path="working" element={<WorkingModule />} />
                <Route path="yard" element={<YardModule />} />
                <Route path="boq" element={<BOQModule />} />
                <Route path="batching-plant" element={<BatchingPlantModule />} />
                <Route path="analysis" element={<AnalysisDashboardModule />} />
                <Route path="subcontractor-rates" element={<SubcontractorRateModule />} />
                <Route path="client-billing" element={<ClientBillingModule />} />
                <Route path="villa-sales" element={<VillaSalesModule />} />
                <Route path="drawings" element={<DrawingsModule />} />
                <Route path="progress-tracking" element={<ProgressTrackingModule />} />
                <Route path="salary" element={<SalaryModule />} />
                <Route path="bg-management" element={<BGModule />} />
                <Route path="tenders" element={<TenderManagementModule />} />
                <Route path="tender-intelligence" element={<TenderIntelligenceModule />} />
                <Route path="item-knowledge-base" element={<ItemKnowledgeBaseModule />} />
                <Route path="procurement" element={<ProcurementVendorModule />} />
                <Route path="ai-integration" element={<AIIntegrationModule />} />
                <Route path="accountant-workbook" element={<DocumentManagerModule />} />
                <Route path="attendance" element={<AttendanceModule />} />
                <Route path="weighbridge" element={<WeighbridgeModule />} />
                <Route path="digital-viewer" element={<DigitalViewerModule />} />
                <Route path="construction-tracker" element={<ConstructionActivityTrackerModule />} />
                <Route path="whatsapp-intelligence" element={<WhatsAppIntelligenceModule />} />
                <Route path="recruitment" element={<RecruitmentModule />} />
                <Route path="po-management" element={<POManagementModule />} />
              </Route>
            </Routes>
          </Router>
          </ProjectProvider>
        </RBACProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
